'use client';

import { useState } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useGeo } from '@/components/GeoProvider';
// Client-safe: jsonld.ts has no server-only imports (only lib/geo + lib/breadcrumbs, both pure).
import { jsonLdHtml } from '@/lib/jsonld';

/* ── Check / Cross icons ── */

function Check({ className }: { className?: string }) {
  return (
    <svg className={`w-4 h-4 shrink-0 ${className ?? 'text-green-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function Cross() {
  return (
    <svg className="w-4 h-4 shrink-0 text-stone-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

/* ── Feature list per plan ─────────────────────────────────────
 * true  = included
 * false = not included
 * ──────────────────────────────────────────────────────────── */

interface PlanFeature {
  name: string;
  standard: boolean;
  premium: boolean;
  elite: boolean;
}

interface FeatureGroup {
  title: string;
  features: PlanFeature[];
}

const allFeatures: FeatureGroup[] = [
  {
    title: 'Mobile App',
    features: [
      { name: 'Android', standard: true, premium: true, elite: true },
      { name: 'iOS', standard: true, premium: true, elite: true },
    ],
  },
  {
    title: 'Dashboard',
    features: [
      { name: 'New Joiners', standard: true, premium: true, elite: true },
      { name: 'Birthdays', standard: true, premium: true, elite: true },
      { name: 'Department Members', standard: true, premium: true, elite: true },
      { name: 'Work Anniversaries', standard: true, premium: true, elite: true },
      { name: 'Leave Availabilities', standard: true, premium: true, elite: true },
      { name: 'Upcoming Holidays', standard: true, premium: true, elite: true },
      { name: 'Important Links', standard: true, premium: true, elite: true },
    ],
  },
  {
    title: 'Employee Management',
    features: [
      { name: 'Employee Profile', standard: true, premium: true, elite: true },
      { name: 'Employee Self Service', standard: true, premium: true, elite: true },
      { name: 'Employee Directory', standard: true, premium: true, elite: true },
      { name: 'Secured Document Storage', standard: true, premium: true, elite: true },
      { name: 'Employee Inbox', standard: true, premium: true, elite: true },
      { name: 'Office 365 Single Sign-On', standard: false, premium: true, elite: true },
    ],
  },
  {
    title: 'Leave Tracker',
    features: [
      { name: 'Sophisticated Leave Policies', standard: true, premium: true, elite: true },
      { name: 'Location-Specific Calendar', standard: true, premium: true, elite: true },
      { name: 'Location-Specific Public Holidays', standard: true, premium: true, elite: true },
      { name: 'Restricted (Floating) Holidays', standard: true, premium: true, elite: true },
      { name: 'Employee Time-Off Dashboard', standard: true, premium: true, elite: true },
      { name: 'Time-Off Requests & Approvals', standard: true, premium: true, elite: true },
      { name: 'Approval Delegation', standard: true, premium: true, elite: true },
      { name: 'Carry Forward & Encashment', standard: true, premium: true, elite: true },
      { name: 'Leave Balance Customisation', standard: true, premium: true, elite: true },
      { name: 'Compensatory Time-Off', standard: true, premium: true, elite: true },
    ],
  },
  {
    title: 'Attendance Tracker',
    features: [
      { name: 'Work Shift Configuration', standard: true, premium: true, elite: true },
      { name: 'Manual Web Check-in / Check-out', standard: true, premium: true, elite: true },
      { name: 'Employee Attendance Dashboard', standard: true, premium: true, elite: true },
      { name: 'Daily Attendance Report', standard: true, premium: true, elite: true },
      { name: 'Team Attendance Report', standard: true, premium: true, elite: true },
      { name: 'Team Early / Late Report', standard: true, premium: true, elite: true },
      { name: 'Geo-fencing', standard: false, premium: true, elite: true },
      { name: 'Biometric Check-in / Check-out', standard: false, premium: true, elite: true },
      { name: 'Employee Shift Scheduling', standard: false, premium: true, elite: true },
      { name: 'Automated Overtime Calculation', standard: false, premium: true, elite: true },
      { name: 'Automated Web Check-in / Check-out', standard: false, premium: false, elite: true },
    ],
  },
  {
    title: 'Payroll',
    features: [
      { name: 'Customisable Earning Components', standard: false, premium: true, elite: true },
      { name: 'Statutory Components', standard: false, premium: true, elite: true },
      { name: 'Salary Templates', standard: false, premium: true, elite: true },
      { name: 'Payslip Generation & Distribution', standard: false, premium: true, elite: true },
      { name: 'Loans & Salary Advances', standard: false, premium: true, elite: true },
      { name: 'Expense & Reimbursements', standard: false, premium: true, elite: true },
      { name: 'Pay Advice', standard: false, premium: true, elite: true },
    ],
  },
  {
    title: 'Collaboration',
    features: [
      { name: 'Timeline & News Feed', standard: true, premium: true, elite: true },
      { name: 'Announcements', standard: true, premium: true, elite: true },
      { name: 'Photo Albums & Media Sharing', standard: true, premium: true, elite: true },
      { name: 'Comments, Replies & Reactions', standard: true, premium: true, elite: true },
      { name: 'News Feed Moderations', standard: true, premium: true, elite: true },
      { name: 'Targeted Feed Sharing', standard: true, premium: true, elite: true },
    ],
  },
  {
    title: 'Project Management',
    features: [
      { name: 'Timesheet', standard: true, premium: true, elite: true },
      { name: 'Task Board', standard: true, premium: true, elite: true },
      { name: 'Invoice Generation', standard: true, premium: true, elite: true },
      { name: 'Reports', standard: false, premium: true, elite: true },
    ],
  },
  {
    title: 'Asset Management',
    features: [
      { name: 'Asset Directory', standard: true, premium: true, elite: true },
      { name: 'Barcode Generation', standard: true, premium: true, elite: true },
      { name: 'Asset Tracking', standard: true, premium: true, elite: true },
      { name: 'Asset Usage History', standard: true, premium: true, elite: true },
      { name: 'Automated Asset Config Fetching', standard: false, premium: false, elite: true },
      { name: 'Asset Config Monitoring', standard: false, premium: false, elite: true },
    ],
  },
  {
    title: 'Employee Activity Monitoring',
    features: [
      { name: 'Individual Daily Productivity', standard: false, premium: false, elite: true },
      { name: 'Most Used Applications / Websites', standard: false, premium: false, elite: true },
      { name: 'Time Spent Per Application', standard: false, premium: false, elite: true },
      { name: 'Real-time Activity', standard: false, premium: false, elite: true },
      { name: 'Active / Idle Time Calculation', standard: false, premium: false, elite: true },
      { name: 'Keystroke & Screenshot Recording', standard: false, premium: false, elite: true },
      { name: 'Team Productivity Reports', standard: false, premium: false, elite: true },
    ],
  },
  {
    title: 'Performance Management',
    features: [
      { name: 'Flexible Bucket Types Configuration', standard: false, premium: true, elite: true },
      { name: 'Configurable Review Cycles', standard: false, premium: true, elite: true },
      { name: 'Short-Term Goals Tracking', standard: false, premium: true, elite: true },
      { name: 'Band-Specific Cycle Questions', standard: false, premium: true, elite: true },
      { name: 'Self / Manager Assessment', standard: false, premium: true, elite: true },
    ],
  },
  {
    title: 'Other Features',
    features: [
      { name: 'Robust Custom Role Configuration', standard: true, premium: true, elite: true },
      { name: 'Privilege-Based Access (Field Level)', standard: true, premium: true, elite: true },
      { name: 'Configurable Email Notifications', standard: true, premium: true, elite: true },
      { name: 'Customisable Email Templates', standard: true, premium: true, elite: true },
    ],
  },
];

/* Helper: get features for plan rendering */
function getFeatureGroups(plan: 'standard' | 'premium' | 'elite') {
  return allFeatures.map((group) => ({
    title: group.title,
    features: group.features,
    plan,
  }));
}

/* Count included features per plan */
const standardCount = allFeatures.reduce((sum, g) => sum + g.features.filter((f) => f.standard).length, 0);
const premiumCount = allFeatures.reduce((sum, g) => sum + g.features.filter((f) => f.premium).length, 0);
const eliteCount = allFeatures.reduce((sum, g) => sum + g.features.filter((f) => f.elite).length, 0);

/* ── FAQ data ── */

const faqs = [
  {
    q: 'Is the Standard plan really free forever?',
    a: 'Yes! The Standard plan supports up to 5 users with attendance, leave management, employee database, collaboration, project management, and mobile app — free forever. No credit card required. From the 6th user, a small per-user fee applies.',
  },
  {
    q: 'Can I switch between monthly and yearly billing?',
    a: 'Absolutely. You can switch at any time from your billing settings. When switching to annual billing, your savings apply immediately to the next billing cycle.',
  },
  {
    q: 'What happens when I outgrow the Standard plan?',
    a: 'Simply upgrade to Premium. All your data, configurations, and history carry over seamlessly — zero downtime. You can also start a free trial of Premium before committing.',
  },
  {
    q: 'Do you offer discounts for large teams?',
    a: 'Yes. Our Elite plan includes volume discounts, custom SLAs, and dedicated account management. Contact our sales team for a personalised quote.',
  },
  {
    q: 'Can I cancel anytime?',
    a: 'Yes, cancel anytime from your account settings. No cancellation fees. Your data remains accessible for 30 days after cancellation.',
  },
  {
    q: 'Is my data secure?',
    a: 'We use client-side AES-256 encryption, SOC 2 compliant infrastructure, role-based access controls, and regular security audits. Your data is stored in certified data centres with 99.9% uptime.',
  },
  {
    q: 'Do you offer a free trial of Premium?',
    a: 'Yes, every new sign-up gets a free trial of all Premium features. No credit card required. After the trial, you can continue on the Standard plan or upgrade.',
  },
];

/* ── JSON-LD ── */

const jsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'OfficePortal HRMS Pricing',
    description:
      'Compare OfficePortal HRMS plans: Standard, Premium, and Elite. See features, pricing, and find the right plan for your business.',
    url: `${process.env.NEXT_PUBLIC_SITE_URL || 'https://www.officeportal.io'}/pricing`,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  },
];

/* ═══════════════════════════════════════════════════════════════
 * ░░ PRICING PAGE ░░
 * ═══════════════════════════════════════════════════════════════ */

export default function PricingPageClient() {
  const { region, loading } = useGeo();
  const [annual, setAnnual] = useState(true);
  const [mobileTab, setMobileTab] = useState<'standard' | 'premium' | 'elite'>('premium');

  const sym = region.currencySymbol;
  const stdExtra = annual ? region.pricing.standardExtraYearly : region.pricing.standardExtra;
  const premPrice = annual ? region.pricing.premiumYearly : region.pricing.premium;
  const eliPrice = annual ? region.pricing.eliteYearly : region.pricing.elite;

  const savePct = Math.round((1 - region.pricing.premiumYearly / region.pricing.premium) * 100);

  /* Plan definitions */
  const plans: PlanDef[] = [
    {
      key: 'standard',
      label: 'Standard',
      description: 'For small teams getting started',
      priceMain: 'FREE',
      priceSub: loading ? '…' : `${sym} ${stdExtra} / user / month from 6th user`,
      accentColor: 'var(--color-t-primary-light)',
      cta: 'Start Free',
      ctaLink: '/setup-free-account',
      count: standardCount,
    },
    {
      key: 'premium',
      label: 'Premium',
      description: 'For growing teams that need more',
      priceMain: loading ? '' : `${sym}${premPrice}`,
      priceSub: 'User Per Month',
      accentColor: 'var(--color-t-cta)',
      ctaTextColor: 'var(--color-t-cta-text)',
      highlighted: true,
      cta: 'Start Free',
      ctaLink: '/setup-free-account',
      count: premiumCount,
    },
    {
      key: 'elite',
      label: 'Elite',
      description: 'For large organisations',
      priceMain: loading ? '' : `${sym}${eliPrice}`,
      priceSub: 'User Per Month',
      accentColor: 'var(--color-t-primary)',
      cta: 'Start Free',
      ctaLink: '/setup-free-account',
      count: eliteCount,
    },
  ];

  const activeMobilePlan = plans.find((p) => p.key === mobileTab)!;

  return (
    <>
      {jsonLd.map((schema, i) => (
        <script
          key={i}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: jsonLdHtml(schema) }}
        />
      ))}

      <Header />

      <main className="min-h-screen">
        {/* ─── Hero ─── */}
        <section
          className="relative pt-28 pb-16 overflow-hidden"
          style={{
            background:
              'var(--t-gradient, linear-gradient(135deg, var(--color-t-primary) 0%, var(--color-t-primary-dark) 100%))',
          }}
        >
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-10 bg-white" />
          <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full opacity-10 bg-white" />

          <div className="relative max-w-7xl mx-auto px-6 lg:px-8 text-center">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-3">
              Best prices to fit your needs
            </h1>
            <p className="text-base sm:text-lg text-white/70 max-w-2xl mx-auto mb-8">
              Choose your plan. Cancel anytime.
            </p>

            {/* ── Billing toggle ── */}
            <div className="relative z-20 inline-flex items-center rounded-full bg-white/10 p-1 backdrop-blur-sm">
              <button
                onClick={() => setAnnual(false)}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
                  !annual ? 'bg-white text-stone-900 shadow-md' : 'text-white/70 hover:text-white'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setAnnual(true)}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
                  annual ? 'bg-white text-stone-900 shadow-md' : 'text-white/70 hover:text-white'
                }`}
              >
                Yearly
                {savePct > 0 && (
                  <span
                    className="ml-1.5 text-xs font-bold"
                    style={annual ? { color: '#16a34a' } : { color: 'rgba(74,222,128,0.8)' }}
                  >
                    Save {savePct}%
                  </span>
                )}
              </button>
            </div>
          </div>
        </section>

        {/* ─── Pricing Cards ─── */}
        <section
          className="relative pt-8 pb-16 lg:pb-24"
          style={{
            background:
              'var(--t-gradient, linear-gradient(135deg, var(--color-t-primary) 0%, var(--color-t-primary-dark) 100%))',
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

            {/* Mobile plan tabs */}
            <div className="flex lg:hidden justify-center mb-6 mt-6">
              <div className="inline-flex rounded-xl bg-white/10 p-1 backdrop-blur-sm">
                {plans.map((p) => (
                  <button
                    key={p.key}
                    onClick={() => setMobileTab(p.key)}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                      mobileTab === p.key
                        ? 'bg-white text-stone-900 shadow-md'
                        : 'text-white/70 hover:text-white'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Desktop: 3 cards side by side */}
            <div className="hidden lg:grid lg:grid-cols-3 gap-6 max-w-6xl mx-auto items-start">
              {plans.map((p) => (
                <PlanCard key={p.key} def={p} features={getFeatureGroups(p.key)} totalCount={eliteCount} loading={loading} />
              ))}
            </div>

            {/* Mobile: single active card */}
            <div className="lg:hidden max-w-md mx-auto">
              <PlanCard
                def={activeMobilePlan}
                features={getFeatureGroups(activeMobilePlan.key)}
                totalCount={eliteCount}
                loading={loading}
                mobile
              />
            </div>

            {/* Trust strip */}
            <div className="mt-16 flex flex-wrap items-center justify-center gap-6 text-white/50 text-xs">
              {[
                { icon: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z', label: 'AES-256 Encrypted' },
                { icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z', label: 'SOC 2 Compliant' },
                { icon: 'M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z', label: '99.9% Uptime' },
                { icon: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z', label: 'No spam, ever' },
              ].map((b) => (
                <span key={b.label} className="flex items-center gap-1.5">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={b.icon} />
                  </svg>
                  {b.label}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ─── FAQ ─── */}
        <section className="py-16 lg:py-20 bg-white">
          <div className="max-w-3xl mx-auto px-6 lg:px-8">
            <h2
              className="text-2xl sm:text-3xl font-bold text-center mb-10"
              style={{ color: 'var(--color-t-heading)' }}
            >
              Frequently asked questions
            </h2>
            <div className="space-y-4">
              {faqs.map((faq) => (
                <details
                  key={faq.q}
                  className="group border border-gray-200 rounded-xl"
                  style={{ backgroundColor: 'var(--color-t-surface)' }}
                >
                  <summary
                    className="flex items-center justify-between cursor-pointer p-5 font-semibold text-sm sm:text-base"
                    style={{ color: 'var(--color-t-heading)' }}
                  >
                    {faq.q}
                    <svg
                      className="w-5 h-5 shrink-0 transition-transform group-open:rotate-45"
                      style={{ color: 'var(--color-t-primary)' }}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </summary>
                  <div className="px-5 pb-5 text-sm leading-relaxed" style={{ color: 'var(--color-t-body)' }}>
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* ─── AEO Entity Paragraph ─── */}
        <section className="py-10" style={{ backgroundColor: 'var(--color-t-surface)' }}>
          <div className="max-w-3xl mx-auto px-6 lg:px-8">
            <p className="text-sm leading-relaxed text-center" style={{ color: 'var(--color-t-body)' }}>
              <strong style={{ color: 'var(--color-t-heading)' }}>OfficePortal</strong> offers
              three pricing tiers — Standard, Premium, and Elite — for its all-in-one HRMS platform.
              The Standard plan supports up to 5 users free with attendance, leave management,
              employee database, collaboration, asset management, and a mobile app. The Premium plan
              at {loading ? '…' : `${sym}${premPrice}`}/user/month adds payroll, geo-fencing,
              biometric attendance, shift scheduling, performance management, and priority support.
              The Elite plan at {loading ? '…' : `${sym}${eliPrice}`}/user/month adds employee
              activity monitoring, automated asset management, custom integrations, a dedicated
              account manager, and SLA compliance reports. All plans include AES-256 encryption,
              SOC 2 compliant infrastructure, and 99.9% uptime.
            </p>
          </div>
        </section>

        {/* ─── Bottom CTA ─── */}
        <section className="py-14 bg-white">
          <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-2xl font-bold mb-3" style={{ color: 'var(--color-t-heading)' }}>
              Ready to get started?
            </h2>
            <p className="mb-6" style={{ color: 'var(--color-t-body)' }}>
              Free for up to 5 users, forever. No credit card required.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                href="/setup-free-account"
                className="inline-flex items-center gap-2 rounded-full px-8 py-3.5 text-base font-semibold shadow-lg transition hover:scale-105 text-white"
                style={{ backgroundColor: 'var(--color-t-primary)' }}
              >
                Start Free
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Link>
              <Link
                href="/schedule-a-demo"
                className="inline-flex items-center gap-2 text-sm font-medium transition hover:opacity-80"
                style={{ color: 'var(--color-t-primary)' }}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Or book a demo
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════
 * ░░ PLAN CARD — full feature list inside each card ░░
 * ═══════════════════════════════════════════════════════════════ */

interface PlanDef {
  key: 'standard' | 'premium' | 'elite';
  label: string;
  description: string;
  priceMain: string;
  priceSub: string;
  accentColor: string;
  ctaTextColor?: string;
  highlighted?: boolean;
  cta: string;
  ctaLink: string;
  count: number;
}

function PlanCard({
  def,
  features,
  totalCount,
  loading,
  mobile,
}: {
  def: PlanDef;
  features: { title: string; features: PlanFeature[]; plan: string }[];
  totalCount: number;
  loading: boolean;
  mobile?: boolean;
}) {
  const { key, label, description, priceMain, priceSub, accentColor, ctaTextColor, highlighted, cta, ctaLink, count } = def;

  return (
    <div
      className={`flex flex-col rounded-2xl ${
        highlighted
          ? 'shadow-2xl z-10 border-t-4'
          : 'shadow-lg'
      }`}
      style={{
        backgroundColor: 'var(--color-t-surface)',
        borderColor: highlighted ? accentColor : undefined,
        borderTopColor: highlighted ? accentColor : undefined,
      }}
    >
      {/* Header */}
      <div
        className="px-7 pt-7 pb-5"
        style={{ borderBottom: '1px solid color-mix(in srgb, var(--color-t-primary) 12%, transparent)' }}
      >
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-2xl font-bold" style={{ color: highlighted ? accentColor : 'var(--color-t-heading)' }}>
            {label}
          </h2>
          {highlighted && (
            <span
              className="px-3 py-1 rounded-full text-[11px] font-bold text-white uppercase tracking-wide"
              style={{ backgroundColor: accentColor }}
            >
              Popular
            </span>
          )}
        </div>
        <p className="text-sm mb-5" style={{ color: 'var(--color-t-heading)', opacity: 0.7 }}>{description}</p>

        {/* Price */}
        <div className="mb-4">
          {loading && priceMain !== 'FREE' ? (
            <span
              className="inline-block w-28 h-14 rounded animate-pulse"
              style={{ backgroundColor: 'color-mix(in srgb, var(--color-t-primary) 10%, transparent)' }}
            />
          ) : (
            <span className="text-5xl font-extrabold tracking-tight" style={{ color: 'var(--color-t-heading)' }}>
              {priceMain}
            </span>
          )}
          <p className="text-sm mt-1 font-medium" style={{ color: 'var(--color-t-heading)', opacity: 0.6 }}>{priceSub}</p>
        </div>

        {/* Feature count bar */}
        <div className="mb-5">
          <span className="text-xs font-semibold" style={{ color: 'var(--color-t-heading)', opacity: 0.65 }}>
            {count} of {totalCount} features included
          </span>
          <div
            className="mt-2 h-2 rounded-full overflow-hidden"
            style={{ backgroundColor: 'color-mix(in srgb, var(--color-t-primary) 10%, transparent)' }}
          >
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${(count / totalCount) * 100}%`, backgroundColor: accentColor }}
            />
          </div>
        </div>

        {/* CTA */}
        <Link
          href={ctaLink}
          className="block w-full py-3.5 rounded-xl text-center font-bold text-[15px] transition-all hover:opacity-90 hover:shadow-lg"
          style={{ backgroundColor: accentColor, color: ctaTextColor || '#FFFFFF' }}
        >
          {cta}
        </Link>
      </div>

      {/* Full feature list grouped by category */}
      <div className="flex-1 px-7 py-6 space-y-6">
        {features.map(({ title, features: items, plan }) => {
          const planKey = plan as 'standard' | 'premium' | 'elite';
          const hasAny = items.some((f) => f[planKey]);
          return (
            <div key={title}>
              <h3
                className="text-xs font-bold uppercase tracking-wider mb-3"
                style={{ color: hasAny ? accentColor : 'var(--color-t-heading)', opacity: hasAny ? 1 : 0.35 }}
              >
                {title}
              </h3>
              <ul className="space-y-2">
                {items.map((feat) => {
                  const included = feat[planKey];
                  return (
                    <li
                      key={feat.name}
                      className={`flex items-center gap-2.5 text-sm leading-normal ${included ? '' : 'opacity-30'}`}
                      style={{ color: 'var(--color-t-heading)' }}
                    >
                      {included ? <Check /> : <Cross />}
                      <span className={`${included ? 'font-medium' : 'line-through font-normal'}`}>{feat.name}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}
